import { AlertTriangle, Inbox, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Standard page states. Every metrics page renders exactly one of:
 * loading skeleton, error (never silent zeros), empty, or content.
 */

export function PageLoading({ cards = 4, chart = true }: { cards?: number; chart?: boolean }) {
  return (
    <div className="space-y-4 pt-4" data-testid="page-loading">
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {Array.from({ length: cards }, (_, i) => (
          <Card key={i}>
            <CardHeader className="pb-2">
              <Skeleton className="h-4 w-24" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-7 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
      {chart && (
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-[280px] w-full" />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

interface PageErrorProps {
  error: string | Error
  onRetry?: () => void
  retrying?: boolean
}

/** Loud error state — a failed fetch must never render as $0 dashboards. */
export function PageError({ error, onRetry, retrying }: PageErrorProps) {
  const message = error instanceof Error ? error.message : error
  return (
    <Card className="mt-4 border-destructive/40" data-testid="page-error">
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-destructive/10">
          <AlertTriangle className="h-5 w-5 text-destructive" />
        </div>
        <div>
          <p className="font-medium text-foreground">Couldn't load this data</p>
          <p className="mt-1 max-w-md text-sm text-muted-foreground">{message}</p>
        </div>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
            <RefreshCw className={retrying ? 'mr-2 h-4 w-4 animate-spin' : 'mr-2 h-4 w-4'} />
            Try again
          </Button>
        )}
      </CardContent>
    </Card>
  )
}

interface EmptyStateProps {
  title?: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
}

export function EmptyState({
  title = 'Nothing here yet',
  description,
  icon,
  action,
}: EmptyStateProps) {
  return (
    <Card className="mt-4 border-dashed" data-testid="page-empty">
      <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
        <div className="flex h-11 w-11 items-center justify-center rounded-full bg-muted">
          {icon ?? <Inbox className="h-5 w-5 text-muted-foreground" />}
        </div>
        <div>
          <p className="font-medium text-foreground">{title}</p>
          {description && (
            <p className="mt-1 max-w-md text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        {action}
      </CardContent>
    </Card>
  )
}
